"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContractUtils = void 0;
const ethers_1 = require("ethers");
const abis_1 = require("../constants/abis");
const variables_1 = require("../constants/variables");
/**
 * A utility class providing methods to interact with contracts.
 * `ContractsUtils` simplifies tasks such as reading from a contract,
 * encoding data for contract calls, and signing off-chain transactions.
 */
class ContractUtils {
    /**
     * Reads data from a deployed contract using the specified function and parameters.
     * @param provider is the ethers.Provider instance to interact with the Fuse network.
     * @param contractAddress is the address of the deployed contract.
     * @param contractName is the name of the contract.
     * @param methodName is the name of the function to be called.
     * @param params is a list of parameters to be passed to the function.
     * @param jsonInterface  is an optional JSON string representing the contract ABI.
     * @returns  a Promise that resolves to a list of values returned by the contract function.
     */
    static readFromContract(provider, contractAddress, contractName, methodName, params = [], jsonInterface) {
        return __awaiter(this, void 0, void 0, function* () {
            const contract = ContractUtils._getDeployedContract(contractName, contractAddress, jsonInterface, provider);
            const result = yield contract.callStatic[methodName](...params);
            return result;
        });
    }
    static encodeERC721ApproveCall(spender, tokenId) {
        const iface = new ethers_1.ethers.utils.Interface(abis_1.ABI.get('ERC721'));
        return iface.encodeFunctionData('approve', [spender, tokenId]);
    }
    static encodeERC271SafeTransferFromCall(from, to, tokenId) {
        const iface = new ethers_1.ethers.utils.Interface(abis_1.ABI.get('ERC721'));
        return iface.encodeFunctionData('safeTransferFrom', [from, to, tokenId]);
    }
    static encodeERC20ApproveCall(spender, amount) {
        const iface = new ethers_1.ethers.utils.Interface(abis_1.ABI.get('ERC20'));
        return iface.encodeFunctionData('approve', [spender, amount]);
    }
    static encodeERC20TransferCall(recipient, amount) {
        const iface = new ethers_1.ethers.utils.Interface(abis_1.ABI.get('ERC20'));
        return iface.encodeFunctionData('transfer', [recipient, amount]);
    }
    /**
   * Checks if the given address is the native token's address.
   * @param tokenAddress is the address to be checked.
   */
    static isNativeToken(tokenAddress) {
        return tokenAddress.toLowerCase() === variables_1.Variables.NATIVE_TOKEN_ADDRESS.toLowerCase();
    }
    /**
     * Reads data from a specified contract and returns the first result as BigInt.
     * @param provider is the ethers.Provider instance to interact with the Fuse network.
     * @param contractAddress is the address of the deployed contract.
     * @param contractName is the name of the contract.
     * @param methodName is the name of the function to be called.
     * @param params is a list of parameters to be passed to the function.
     * @param jsonInterface  is an optional JSON string representing the contract ABI.
     * @returns  a Promise that resolves to a list of values returned by the contract function.
     */
    static readFromContractWithFirstResult(provider, contractAddress, contractName, methodName, params = []) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield ContractUtils.readFromContract(provider, contractAddress, contractName, methodName, params);
            return result;
        });
    }
    /**
     *  Retrieves the deployed contract instance using the contract name and address.
     * @param contractName is the name of the contract.
     * @param contractAddress is the address of the deployed contract.
     * @param jsonInterface is an optional JSON string representing the contract ABI.
     * @returns  Returns an intance of ethers.Contract
     */
    static _getDeployedContract(contractName, contractAddress, jsonInterface, provider) {
        const abi = jsonInterface !== null && jsonInterface !== void 0 ? jsonInterface : abis_1.ABI.get(contractName);
        let contract;
        if (provider)
            contract = new ethers_1.ethers.Contract(contractAddress, abi, provider);
        else
            contract = new ethers_1.ethers.Contract(contractAddress, abi);
        return contract;
    }
}
exports.ContractUtils = ContractUtils;
